import { useTranslation } from 'react-i18next';

const FEATURE_ICONS = [
  'ri-speed-line',
  'ri-lock-line',
  'ri-stack-line',
  'ri-customer-service-line',
  'ri-refresh-line',
  'ri-global-line',
] as const;

type FeatureItem = { title: string; description: string };

export default function Features() {
  const { t } = useTranslation();
  const items = t('features.items', { returnObjects: true }) as FeatureItem[];
  const features = items.map((item, index) => ({
    ...item,
    icon: FEATURE_ICONS[index] ?? 'ri-star-line',
  }));

  return (
    <section id="products" className="py-24 bg-slate-950 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(6, 182, 212, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(6, 182, 212, 0.1) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full backdrop-blur-sm mb-6">
            <i className="ri-star-line text-cyan-400"></i>
            <span className="text-cyan-400 text-sm font-medium">{t('features.badge')}</span>
          </div>
          <h4 className="text-4xl lg:text-5xl font-black text-white mb-6">
            {t('features.title')}
          </h4>
          <p className="text-lg text-gray-400 max-w-3xl mx-auto">
            {t('features.description')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className="group p-8 rounded-2xl bg-gradient-to-br from-slate-900/50 to-slate-900/30 border border-cyan-500/10 hover:border-cyan-500/30 transition-all duration-300 backdrop-blur-sm hover:shadow-xl hover:shadow-cyan-500/10 flex flex-col items-center text-center"
            >
              <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500/20 to-teal-500/20 border border-cyan-500/30 mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className={`${feature.icon} text-2xl text-cyan-400`}></i>
              </div>

              <h5 className="text-xl font-bold text-white mb-3">
                {feature.title}
              </h5>

              <p className="text-gray-400 text-sm leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
